import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { RecursivePartial } from '@models/apollo';
import { LicenseType } from '@models/license';
import { IlClass, Invite, Learner, License, TagAttributes } from '@queries/generated';
import { LearnersService } from '@services/learners.service';
import { Fade, Reveal } from '@util/animations';
import { Observable, of } from 'rxjs';
import { concatAll } from 'rxjs/operators';
import { FilledTagField } from '../assign-training-wizard/assign-training-wizard.component';

interface Booking {
  license: LicenseType;
  ilClass: IlClass;
}

@Component({
  selector: 'vnm-assign-training',
  templateUrl: './assign-training.component.html',
  styleUrls: ['./assign-training.component.scss'],
  animations: [Fade, Reveal]
})
export class AssignTrainingComponent implements OnInit {
  @HostBinding('class.has-errors')
  get hasErrors() {
    return this.errors.length > 0;
  }

  @HostBinding('class.has-assigned')
  get hasAssigned() {
    return this.invites.length > 0;
  }

  @Input() learners: Learner[];
  @Input() license: License;
  @Input() bookings: Booking[];
  @Input() tags: FilledTagField[];

  @Output() assigned = new EventEmitter<RecursivePartial<Invite>[]>();

  invites: RecursivePartial<Invite>[] = [];
  errors = [];
  running = true;

  constructor(private learnersService: LearnersService) {}

  private assignments: Observable<RecursivePartial<Invite>>[] = [];

  ngOnInit() {
    this.buildAssignments();
    this.performAssignments();
  }

  private buildAssignments() {
    const { bookings, learners, license } = this;

    learners.forEach((learner) => {
      this.assignments.push(
        this.learnersService.assignTraining({
          licenseId: license.gid,
          ilClassIds: bookings.map((booking) => booking.ilClass.gid),
          learnerId: learner.gid,
          tags: this.parsedTags()
        })
      );
    });
  }

  private performAssignments() {
    of(...this.assignments)
      .pipe(concatAll())
      .subscribe(
        (invite) => {
          if (invite) {
            this.invites.push(invite);
          }
        },
        (errors) => {
          this.errors = errors;
          this.running = false;
        },
        () => {
          this.running = false;
          this.assigned.emit(this.invites);
        }
      );
  }

  private parsedTags(): TagAttributes[] {
    return this.tags.map((tag) => ({ key: tag.key, value: tag.value })).filter((tag) => tag.value);
  }
}
